import { IPaginateResponse } from "@/interfaces/Fetch";
import { createFormDataRequest } from "@/helpers/FormHelpers";
import Instructor, {
  InstructorFilters,
  InstructorFormSend,
} from "@/interfaces/Instructor";
import api from "../api";

interface InstructorFilterOptions {
  areas: string[];
  courses: { id: string; name: string }[];
}

export const instructorsApi = api.injectEndpoints({
  endpoints: (builds) => ({
    getInstructorFilterOptions: builds.query<InstructorFilterOptions, void>({
      query: () => "/instructors/filter-options",
      providesTags: (result) => [
        { type: "Instructors", id: "LIST" },
        { type: "Courses", id: "LIST" },
        ...(result?.courses.map(({ id }) => ({
          type: "Courses" as const,
          id,
        })) ?? []),
      ],
    }),
    getInstructors: builds.query<
      IPaginateResponse<Instructor>,
      InstructorFilters
    >({
      query: (params) => ({
        url: "/instructors",
        params: createFormDataRequest(params),
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Instructors" as const,
                id,
              })),
              { type: "Instructors", id: "LIST" },
            ]
          : [{ type: "Instructors", id: "LIST" }],
    }),
    getInstructor: builds.query<Instructor, string>({
      query: (id) => `/instructors/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Instructors", id }],
    }),
    storeInstructor: builds.mutation<Instructor, InstructorFormSend>({
      query: (body) => ({
        url: `/instructors`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Instructors", id: "LIST" }],
    }),
    updateInstructor: builds.mutation<
      Instructor,
      [string, Partial<InstructorFormSend>]
    >({
      query: ([id, body]) => ({
        url: `/instructors/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, [id]) => [
        { type: "Instructors", id },
        { type: "Instructors", id: "LIST" },
      ],
    }),
    deleteInstructor: builds.mutation<void, string>({
      query: (id) => ({
        url: `/instructors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Instructors", id: "LIST" }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetInstructorFilterOptionsQuery,
  useGetInstructorQuery,
  useGetInstructorsQuery,
  useUpdateInstructorMutation,
  useStoreInstructorMutation,
  useDeleteInstructorMutation,
  useLazyGetInstructorsQuery,
} = instructorsApi;
